/**
 * Weekly Coach Digest — visual surface.
 *
 * The "Sunday night recap" view. Builds on the persistent Coach Profile +
 * the trade history to show:
 *
 *   1. Coach Voice (LLM-generated weekly narrative, when enabled)
 *   2. Headline + week's performance (P&L, WR, biggest win/loss, top setup)
 *   3. Profile movement (new/persisted/resolved/regressed/ascended edges + leaks)
 *   4. Top 1-3 actionables to focus on next week
 *
 * Renders nothing meaningful until the user has built a Coach Profile + has
 * trades in the last 7 days. Empty state explains how to get there.
 *
 * Props:
 *   trades            All closed trades (will be window-filtered internally)
 *   profile           Current CoachProfile from useCoachProfile
 *   profileHistory    History snapshots from useCoachProfile
 *   getTradeNetPnL    (trade, account) => { netPnl }
 *   getAccountById    (accountId) => account|null
 *   llmNarrator       Optional: hook output from useLlmNarrator
 *   onClose           () => void
 *   darkMode          bool
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Calendar, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  CheckCircle2, AlertTriangle, Sparkles, Target, MessageSquare, Loader2,
  Clock, Zap, BarChart3,
} from 'lucide-react';
import { buildWeeklyDigest } from '../../services/coach/digest/buildWeeklyDigest';
import CoachSpeakButton from './CoachSpeakButton';

// ────────────────────────────────────────────────────────────────────────
// Formatting helpers
// ────────────────────────────────────────────────────────────────────────

const fmtMoney = (n, opts = {}) => {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(Math.round(n));
  const sign = n >= 0 ? (opts.alwaysSign ? '+' : '') : '−';
  return `${sign}$${abs.toLocaleString()}`;
};
const fmtPct = (n) => {
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
};
const fmtR = (n) => {
  if (!Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}R`;
};
const fmtDateRange = (startIso, endIso) => {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const opts = { month: 'short', day: 'numeric' };
    return `${s.toLocaleDateString(undefined, opts)} → ${e.toLocaleDateString(undefined, opts)}`;
  } catch { return ''; }
};

// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

const PerfTile = ({ label, value, sub, accent = 'sky', darkMode }) => {
  const colorClass = {
    emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
    red:     'text-red-300 bg-red-500/10 border-red-500/20',
    sky:     'text-sky-300 bg-sky-500/10 border-sky-500/20',
    purple:  'text-purple-300 bg-purple-500/10 border-purple-500/20',
    amber:   'text-amber-300 bg-amber-500/10 border-amber-500/20',
  }[accent] || 'text-sky-300 bg-sky-500/10 border-sky-500/20';

  return (
    <div className={`rounded-lg border p-3 ${colorClass}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      {sub && (
        <div className={`text-[11px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {sub}
        </div>
      )}
    </div>
  );
};

const MovementChip = ({ icon: Icon, label, count, accent, darkMode }) => {
  const colorClass = {
    emerald: darkMode ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' : 'bg-emerald-100 border-emerald-300 text-emerald-700',
    amber:   darkMode ? 'bg-amber-500/15 border-amber-500/30 text-amber-300' : 'bg-amber-100 border-amber-300 text-amber-700',
    red:     darkMode ? 'bg-red-500/15 border-red-500/30 text-red-300' : 'bg-red-100 border-red-300 text-red-700',
    sky:     darkMode ? 'bg-sky-500/15 border-sky-500/30 text-sky-300' : 'bg-sky-100 border-sky-300 text-sky-700',
  }[accent] || '';

  return (
    <div className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border ${colorClass}`}>
      <Icon size={11} />
      <span className="font-medium">{count}</span>
      <span className="opacity-90">{label}</span>
    </div>
  );
};

const EntryRow = ({ entry, kind, darkMode }) => {
  const accent = kind === 'edge' || kind === 'ascended'
    ? (darkMode ? 'border-l-emerald-500' : 'border-l-emerald-500')
    : kind === 'leak' || kind === 'regressed'
      ? (darkMode ? 'border-l-amber-500' : 'border-l-amber-500')
      : kind === 'resolved'
        ? (darkMode ? 'border-l-sky-500' : 'border-l-sky-500')
        : 'border-l-gray-500';

  return (
    <div className={`rounded-md border-l-2 ${accent} p-2 ${darkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {entry.scope || ''}
          {entry.scopeId && <span className="opacity-70"> · {entry.scopeId}</span>}
        </span>
        {entry.occurrences > 1 && (
          <span className={`text-[9px] px-1.5 py-px rounded-full ${darkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
            {entry.occurrences}× total
          </span>
        )}
      </div>
      <p className={`text-xs leading-snug ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        {entry.title || entry.signature}
      </p>
    </div>
  );
};

const Section = ({ icon: Icon, title, accent, count, children, darkMode, emptyMessage }) => {
  const accentText = {
    emerald: 'text-emerald-400', amber: 'text-amber-400', red: 'text-red-400',
    sky: 'text-sky-400', purple: 'text-purple-400',
  }[accent] || 'text-sky-400';

  const hasChildren = Array.isArray(children) ? children.filter(Boolean).length > 0 : !!children;
  return (
    <section className="mb-5">
      <div className="flex items-center gap-2 mb-2.5">
        <Icon size={14} className={accentText} />
        <h3 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {title}
        </h3>
        {typeof count === 'number' && (
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>· {count}</span>
        )}
      </div>
      {hasChildren ? children : (
        emptyMessage ? (
          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} italic`}>{emptyMessage}</div>
        ) : null
      )}
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────

const WeeklyDigestModal = ({
  trades = [],
  profile = null,
  profileHistory = [],
  getTradeNetPnL,
  getAccountById,
  llmNarrator,
  onClose,
  darkMode = true,
}) => {
  // Esc to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Build the digest. Memoized on inputs so reopens during a session don't
  // recompute when nothing has changed. The trade list reference being
  // stable matters here — TradingJournal uses useState/useMemo so it should be.
  const digest = useMemo(() => buildWeeklyDigest({
    trades, profile, profileHistory, getTradeNetPnL, getAccountById,
  }), [trades, profile, profileHistory, getTradeNetPnL, getAccountById]);

  // ── LLM Coach Voice — narrate the week ────────────────────────────
  // Uses the profile entries (movement) as the "observation set" for the
  // narrator. Different signature key from the dashboard's call (different
  // scope id) so the cache doesn't conflict.
  const [narrative, setNarrative] = useState({ text: '', loading: false, error: null, fromCache: false });
  const lastSigRef = useRef('');

  // ⚠️ Infinite-loop guard (Phase 16 hotfix): useLlmNarrator returns a fresh
  // object literal every render. Putting `llmNarrator` itself in useEffect
  // deps means the effect fires every render — and when it calls setNarrative
  // (which happens whenever isReady is false), it triggers another render →
  // infinite loop. We capture the latest narrator in a ref and depend only
  // on the primitive `isReady` flag.
  const llmNarratorRef = useRef(llmNarrator);
  useEffect(() => { llmNarratorRef.current = llmNarrator; });
  const isNarratorReady = !!llmNarrator?.isReady;

  // Mounted-state ref (Phase 16 hotfix #3) — Strict-Mode-safe.
  //
  // The naive `let cancelled = false; return () => { cancelled = true; }`
  // pattern (per-effect) cancels in-flight narrates on every re-render,
  // wedging the narrative in permanent loading. So we lifted cancellation
  // to component lifecycle via a ref. BUT a unmount-only cleanup like
  //   useEffect(() => () => { ref.current = false; }, [])
  // is BROKEN in React.StrictMode: dev's double-invocation runs the
  // cleanup once and then re-runs the effect, which doesn't reset the ref
  // back to true → ref stays false forever → async results all bail.
  //
  // Setting the ref to true inside the effect's BODY (not just the cleanup)
  // makes it Strict-Mode safe: each effect run reseats the flag, the
  // cleanup unsets it on actual unmount, and the final state on settle is
  // always true.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Build a stable signature from the digest sections so we only re-narrate
  // when the actual content changes.
  const digestSig = digest
    ? JSON.stringify({
        win: digest.windowStart, end: digest.windowEnd,
        perfNet: digest.performance?.netPnl,
        perfTrades: digest.performance?.tradeCount,
        movKeys: [
          ...(digest.movement.newEdges || []).map(e => e.signature),
          ...(digest.movement.newLeaks || []).map(e => e.signature),
          ...(digest.movement.regressed || []).map(e => e.signature),
          ...(digest.movement.ascended || []).map(e => e.signature),
          ...(digest.movement.resolvedLeaks || []).map(e => e.signature || e.title),
        ].sort(),
      })
    : '';

  useEffect(() => {
    if (!isNarratorReady || !digest) {
      // Guarded reset — only set state if not already empty. Without this
      // guard, a fresh object literal would re-trigger render → infinite loop.
      setNarrative(prev =>
        (prev.text === '' && !prev.loading && !prev.error && !prev.fromCache)
          ? prev
          : { text: '', loading: false, error: null, fromCache: false }
      );
      lastSigRef.current = '';
      return;
    }
    if (digestSig === lastSigRef.current) return;

    // Build observation-shaped objects from the movement buckets so the
    // prompt builder can reason about them. Each gets a synthetic
    // temporalContext that nudges the LLM to recognize what's new vs
    // recurring — the narrator's prompt already knows how to weave that.
    const asObservations = [
      ...(digest.movement.regressed || []).map(e => ({
        ...e, severity: 'warning', temporalContext: { state: 'persistent', runs: 2 },
        title: `Regressed this week: ${e.title}`,
      })),
      ...(digest.movement.ascended || []).map(e => ({
        ...e, severity: 'success', temporalContext: { state: 'returning', runs: 1 },
        title: `Ascended this week: ${e.title}`,
      })),
      ...(digest.movement.persistedLeaks || []).map(e => ({
        ...e, severity: 'warning', temporalContext: { state: 'persistent', runs: e.occurrences || 2 },
      })),
      ...(digest.movement.persistedEdges || []).map(e => ({
        ...e, severity: 'success', temporalContext: { state: 'persistent', runs: e.occurrences || 2 },
      })),
      ...(digest.movement.newLeaks || []).map(e => ({
        ...e, severity: 'warning', temporalContext: { state: 'new', runs: 1 },
      })),
      ...(digest.movement.newEdges || []).map(e => ({
        ...e, severity: 'success', temporalContext: { state: 'new', runs: 1 },
      })),
    ];
    if (asObservations.length === 0) return;

    // Read narrator via ref so we use the latest `narrate` without depending
    // on the unstable object literal.
    const narrator = llmNarratorRef.current;
    if (!narrator?.narrate) return;

    // ⚠️ Set the signature ref BEFORE starting the async call — not after
    // it resolves. Without this, parent re-renders during the in-flight
    // narrate (caused by unstable prop refs like inline `getTradeNetPnL`
    // arrows from TradingJournal) recompute `digest`, fire this effect
    // again, find the ref still empty, and call setNarrative repeatedly.
    // The repeat setNarrative calls cascade into infinite renders + OOM.
    lastSigRef.current = digestSig;
    setNarrative({ text: '', loading: true, error: null, fromCache: false });
    (async () => {
      const result = await narrator.narrate({
        observations: asObservations,
        scope: 'progress',  // weekly = progress-shaped narrative
        scopeId: 'weekly-digest',
        tradeCount: digest.performance?.tradeCount || 0,
      });
      // Mounted-ref check — see the comment on mountedRef above. Replaces
      // the old per-effect `cancelled` flag, which caused the stuck-
      // shimmer-bars wedge when a re-render's cleanup cancelled the
      // in-flight call that the early-return then prevented from replacing.
      if (!mountedRef.current) return;
      if (result.ok) {
        setNarrative({
          text: result.text, loading: false, error: null, fromCache: !!result.fromCache,
        });
      } else {
        // On error, reset the ref so the next render attempts the call again
        // rather than wedging in a permanent "tried that" state.
        lastSigRef.current = '';
        setNarrative({ text: '', loading: false, error: result.error, fromCache: false });
      }
    })();
    // Stable primitive deps only — `isNarratorReady` is a boolean,
    // `digestSig` is a string. Object refs read via the ref above.
    // NOTE: no cleanup returned — see the call-token-pattern comment.
  }, [isNarratorReady, digest, digestSig]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`
          relative w-full max-w-5xl max-h-[90vh] flex flex-col
          rounded-xl shadow-2xl
          ${darkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-200'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className={`flex items-start justify-between gap-3 p-5 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-lg bg-blue-500/20 p-2 border border-blue-500/30 shrink-0">
              <Calendar size={20} className="text-blue-300" />
            </div>
            <div className="min-w-0">
              <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Weekly Coach Digest
              </h2>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {digest ? fmtDateRange(digest.windowStart, digest.windowEnd) : 'No data for this week yet'}
              </p>
              {digest && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {digest.headline}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg shrink-0 ${darkMode ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Body ────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5">
          {!digest && (
            <div className="text-center py-16">
              <div className="inline-flex p-4 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
                <Calendar size={32} className="text-blue-300" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Not enough data yet
              </h3>
              <p className={`text-sm max-w-md mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                The weekly digest needs trades from the last 7 days OR a Coach Profile to compare against. Take a few trades and click <strong>Refresh</strong> in the Coach Dashboard, then come back here.
              </p>
            </div>
          )}

          {digest && (
            <>
              {/* Coach Voice (LLM narrator) */}
              {llmNarrator?.isReady && (
                <div className={`mb-5 rounded-xl border p-4 ${
                  darkMode
                    ? 'bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/25'
                    : 'bg-gradient-to-br from-purple-50 to-white border-purple-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare size={14} className={darkMode ? 'text-purple-300' : 'text-purple-700'} />
                    <h3 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                      Coach Voice — week recap
                    </h3>
                    {narrative.loading && <Loader2 size={11} className={`animate-spin ${darkMode ? 'text-purple-300/70' : 'text-purple-700/70'}`} />}
                    {narrative.fromCache && !narrative.loading && (
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-px rounded-full ${darkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        cached
                      </span>
                    )}
                    {/* Phase 15 — TTS speak/stop button. Same shape as the dashboard's.
                        Phase 16 — threads ttsSettings for provider dispatch. */}
                    {narrative.text && !narrative.loading && (
                      <CoachSpeakButton
                        text={narrative.text}
                        voice={llmNarrator?.settings?.voice || 'mentor'}
                        ttsEnabled={!!llmNarrator?.settings?.ttsEnabled}
                        ttsSettings={llmNarrator?.settings?.tts}
                        darkMode={darkMode}
                      />
                    )}
                  </div>
                  {narrative.loading && !narrative.text && (
                    <div className="space-y-1.5">
                      <div className={`h-3 w-full rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
                      <div className={`h-3 w-11/12 rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
                      <div className={`h-3 w-9/12 rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
                    </div>
                  )}
                  {!narrative.loading && narrative.text && (
                    <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{narrative.text}</p>
                  )}
                  {narrative.error && (
                    <p className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                      Coach Voice unavailable: {narrative.error}.
                    </p>
                  )}
                </div>
              )}

              {/* Performance row */}
              {digest.sections.hasPerformance && (
                <Section icon={BarChart3} title="This week's tape" accent="sky" darkMode={darkMode}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <PerfTile
                      label="Net P&L"
                      value={fmtMoney(digest.performance.netPnl, { alwaysSign: true })}
                      sub={`${digest.performance.tradeCount} trade${digest.performance.tradeCount === 1 ? '' : 's'}`}
                      accent={digest.performance.netPnl >= 0 ? 'emerald' : 'red'}
                      darkMode={darkMode}
                    />
                    <PerfTile
                      label="Win rate"
                      value={fmtPct(digest.performance.winRate)}
                      sub={`${digest.performance.wins}W / ${digest.performance.losses}L`}
                      accent={digest.performance.winRate >= 0.5 ? 'emerald' : 'amber'}
                      darkMode={darkMode}
                    />
                    <PerfTile
                      label="Avg R"
                      value={fmtR(digest.performance.avgR)}
                      sub={digest.performance.avgR != null && digest.performance.avgR > 0 ? 'Profitable risk' : 'Underwater on risk'}
                      accent={digest.performance.avgR != null && digest.performance.avgR > 0 ? 'emerald' : 'red'}
                      darkMode={darkMode}
                    />
                    <PerfTile
                      label="Best setup"
                      value={digest.performance.bestSetup?.name || '—'}
                      sub={digest.performance.bestSetup ? `${fmtMoney(digest.performance.bestSetup.netPnl, { alwaysSign: true })} · ${digest.performance.bestSetup.count}` : ''}
                      accent="purple"
                      darkMode={darkMode}
                    />
                  </div>
                  {(digest.performance.biggestWin || digest.performance.biggestLoss) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {digest.performance.biggestWin && (
                        <div className={`rounded-md border-l-2 border-l-emerald-500 p-2.5 ${darkMode ? 'bg-gray-800/40' : 'bg-emerald-50/50'}`}>
                          <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${darkMode ? 'text-emerald-300/80' : 'text-emerald-700/80'}`}>
                            Biggest win
                          </div>
                          <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {fmtMoney(digest.performance.biggestWin.netPnl, { alwaysSign: true })}
                            <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {digest.performance.biggestWin.setup || 'setup'} · {digest.performance.biggestWin.date}
                            </span>
                          </div>
                        </div>
                      )}
                      {digest.performance.biggestLoss && (
                        <div className={`rounded-md border-l-2 border-l-red-500 p-2.5 ${darkMode ? 'bg-gray-800/40' : 'bg-red-50/50'}`}>
                          <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${darkMode ? 'text-red-300/80' : 'text-red-700/80'}`}>
                            Biggest loss
                          </div>
                          <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {fmtMoney(digest.performance.biggestLoss.netPnl, { alwaysSign: true })}
                            <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {digest.performance.biggestLoss.setup || 'setup'} · {digest.performance.biggestLoss.date}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Section>
              )}

              {/* Profile movement summary chips */}
              <Section
                icon={Zap}
                title={digest.hasComparableSnapshot
                  ? `Profile movement (vs ${new Date(digest.weekAgoSnapshotAt).toLocaleDateString()})`
                  : 'Profile movement (no week-ago snapshot — showing current state)'}
                accent="purple"
                darkMode={darkMode}
              >
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {digest.movement.newEdges.length > 0 && <MovementChip icon={Sparkles} label="new edges" count={digest.movement.newEdges.length} accent="emerald" darkMode={darkMode} />}
                  {digest.movement.ascended.length > 0 && <MovementChip icon={ArrowUpRight} label="ascended" count={digest.movement.ascended.length} accent="emerald" darkMode={darkMode} />}
                  {digest.movement.persistedEdges.length > 0 && <MovementChip icon={CheckCircle2} label="persisted edges" count={digest.movement.persistedEdges.length} accent="sky" darkMode={darkMode} />}
                  {digest.movement.resolvedLeaks.length > 0 && <MovementChip icon={CheckCircle2} label="resolved" count={digest.movement.resolvedLeaks.length} accent="sky" darkMode={darkMode} />}
                  {digest.movement.fadedEdges.length > 0 && <MovementChip icon={TrendingDown} label="faded edges" count={digest.movement.fadedEdges.length} accent="amber" darkMode={darkMode} />}
                  {digest.movement.persistedLeaks.length > 0 && <MovementChip icon={AlertTriangle} label="persistent leaks" count={digest.movement.persistedLeaks.length} accent="amber" darkMode={darkMode} />}
                  {digest.movement.newLeaks.length > 0 && <MovementChip icon={AlertTriangle} label="new leaks" count={digest.movement.newLeaks.length} accent="amber" darkMode={darkMode} />}
                  {digest.movement.regressed.length > 0 && <MovementChip icon={ArrowDownRight} label="regressed" count={digest.movement.regressed.length} accent="red" darkMode={darkMode} />}
                </div>

                {/* Detailed lists, only show non-empty buckets */}
                {digest.movement.regressed.length > 0 && (
                  <div className="mb-3">
                    <div className={`text-[10px] uppercase tracking-wider mb-1.5 ${darkMode ? 'text-red-300/80' : 'text-red-700/80'}`}>
                      Regressed (edge → leak)
                    </div>
                    <div className="space-y-1.5">
                      {digest.movement.regressed.map(e => <EntryRow key={`r-${e.signature}`} entry={e} kind="regressed" darkMode={darkMode} />)}
                    </div>
                  </div>
                )}
                {digest.movement.ascended.length > 0 && (
                  <div className="mb-3">
                    <div className={`text-[10px] uppercase tracking-wider mb-1.5 ${darkMode ? 'text-emerald-300/80' : 'text-emerald-700/80'}`}>
                      Ascended (leak → edge)
                    </div>
                    <div className="space-y-1.5">
                      {digest.movement.ascended.map(e => <EntryRow key={`a-${e.signature}`} entry={e} kind="ascended" darkMode={darkMode} />)}
                    </div>
                  </div>
                )}
                {digest.movement.newEdges.length > 0 && (
                  <div className="mb-3">
                    <div className={`text-[10px] uppercase tracking-wider mb-1.5 ${darkMode ? 'text-emerald-300/80' : 'text-emerald-700/80'}`}>
                      New edges
                    </div>
                    <div className="space-y-1.5">
                      {digest.movement.newEdges.map(e => <EntryRow key={`ne-${e.signature}`} entry={e} kind="edge" darkMode={darkMode} />)}
                    </div>
                  </div>
                )}
                {digest.movement.newLeaks.length > 0 && (
                  <div className="mb-3">
                    <div className={`text-[10px] uppercase tracking-wider mb-1.5 ${darkMode ? 'text-amber-300/80' : 'text-amber-700/80'}`}>
                      New leaks
                    </div>
                    <div className="space-y-1.5">
                      {digest.movement.newLeaks.map(e => <EntryRow key={`nl-${e.signature}`} entry={e} kind="leak" darkMode={darkMode} />)}
                    </div>
                  </div>
                )}
              </Section>

              {/* Top recommendations */}
              {digest.sections.hasRecommendations && (
                <Section icon={Target} title="Focus next week" accent="purple" darkMode={darkMode}>
                  <div className="space-y-2">
                    {digest.recommendations.map((rec, i) => (
                      <div key={i} className={`rounded-md border-l-2 ${rec.kind === 'regression' ? 'border-l-red-500' : rec.kind === 'persistent' ? 'border-l-amber-500' : 'border-l-purple-500'} p-3 ${darkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
                        <div className={`text-[10px] uppercase tracking-wider mb-1 ${darkMode ? 'text-purple-300/80' : 'text-purple-700/80'}`}>
                          {rec.kind === 'regression' ? 'Stop the bleed' : rec.kind === 'persistent' ? 'Chronic — fix it' : 'Fresh issue — nip early'}
                        </div>
                        <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {rec.title}
                        </p>
                        <p className={`text-xs italic ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          → {rec.action}
                        </p>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Footer meta */}
              <div className={`mt-2 pt-3 border-t flex items-center gap-2 text-[11px] ${darkMode ? 'border-white/5 text-gray-500' : 'border-gray-200 text-gray-500'}`}>
                <Clock size={11} />
                <span>
                  Generated {new Date(digest.generatedAt).toLocaleString()} · Window {fmtDateRange(digest.windowStart, digest.windowEnd)}
                  {!digest.hasComparableSnapshot && ' · No comparable snapshot from a week ago — running degraded'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WeeklyDigestModal;
